/**
 * Bridges the API's RFC3339 date-times and the `<input type="datetime-local">`
 * value format (`YYYY-MM-DDTHH:mm`, no timezone, no milliseconds).
 *
 * A `datetime-local` input rejects (per the WHATWG value-sanitization algorithm)
 * any value carrying a timezone designator (`Z` or `+07:00`) or milliseconds,
 * rendering the field empty in a real browser. (jsdom is lenient and keeps the
 * raw value, which is why this bug stays invisible in unit tests.) The builder
 * loaded an existing promotion by binding the raw API value
 * (e.g. `2026-05-30T11:37:00+07:00`) straight into the input — the browser
 * cleared it, the required start/end dates submitted empty, and Save failed Zod
 * validation. Convert IN on load (`toDatetimeLocal`) and OUT on save (`toIso`).
 */

/**
 * RFC3339/ISO instant -> `YYYY-MM-DDTHH:mm` in the viewer's local time.
 * Empty/invalid input -> "" (so the input renders blank rather than rejecting).
 */
export function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * `datetime-local` value (local wall-clock) -> ISO instant for the BE.
 * Empty/invalid input is passed through unchanged (lets Zod surface the error).
 */
export function toIso(local: string): string {
  if (!local) return local;
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? local : parsed.toISOString();
}
